import { all, call, put, takeLatest } from 'redux-saga/effects';
import { CANCEL } from 'redux-saga';
import { comparisonQueryParams } from '../../../utils/reportPeriod';
import type {
  AvgSpendBySegmentResponse,
  AcquisitionSourceResponse,
  CustomerDetailsResponse,
  DrilldownCustomersResponse,
  DrilldownStatsResponse,
  ChannelPerformanceResponse,
  CohortRetentionResponse,
  CustomerDirectoryResponse,
  CustomerSegmentBreakdownResponse,
  CustomerStampKpisResponse,
} from '../../../interface/customersReportInterface';
import {
  FETCH_DRILLDOWN_STATS_REQUEST,
  FETCH_ACQUISITION_SOURCE_REQUEST,
  FETCH_CUSTOMER_DETAILS_REQUEST,
  FETCH_DRILLDOWN_CUSTOMERS_REQUEST,
  FETCH_SEGMENT_SPEND_REQUEST,
  FETCH_CHANNEL_PERFORMANCE_REQUEST,
  FETCH_COHORT_RETENTION_REQUEST,
  FETCH_CUSTOMER_DIRECTORY_REQUEST,
  FETCH_CUSTOMER_SEGMENTS_REQUEST,
  FETCH_CUSTOMER_STAMP_KPIS_REQUEST,
} from './customersReportConstants';
import {
  fetchCustomerStampKpisRequest,
  fetchCustomerStampKpisSuccess,
  fetchCustomerStampKpisFailure,
  fetchCustomerSegmentsRequest,
  fetchCustomerSegmentsSuccess,
  fetchCustomerSegmentsFailure,
  fetchCustomerDirectoryRequest,
  fetchCustomerDirectorySuccess,
  fetchCustomerDirectoryFailure,
  fetchChannelPerformanceRequest,
  fetchChannelPerformanceSuccess,
  fetchChannelPerformanceFailure,
  fetchCohortRetentionRequest,
  fetchCohortRetentionSuccess,
  fetchCohortRetentionFailure,
  fetchSegmentSpendRequest,
  fetchSegmentSpendSuccess,
  fetchSegmentSpendFailure,
  fetchDrilldownStatsRequest,
  fetchDrilldownStatsSuccess,
  fetchDrilldownStatsFailure,
  fetchDrilldownCustomersRequest,
  fetchDrilldownCustomersSuccess,
  fetchDrilldownCustomersFailure,
  fetchCustomerDetailsRequest,
  fetchAcquisitionSourceRequest,
  fetchAcquisitionSourceSuccess,
  fetchAcquisitionSourceFailure,
  fetchCustomerDetailsSuccess,
  fetchCustomerDetailsFailure,
} from './customersReportActions';
import {
  getAvgSpendBySegmentApi,
  getDrilldownStatsApi,
  getAcquisitionSourceApi,
  getCustomerDetailsApi,
  getDrilldownCustomersApi,
  getChannelPerformanceApi,
  getCohortRetentionApi,
  getCustomerDirectoryApi,
  getCustomerSegmentBreakdownApi,
  getCustomerStampKpisApi,
} from './customersReportApi';

/* Same abortable wrapper as voiceAgentSaga: when `takeLatest` cancels a stale
   task (quick date/location change), the in-flight fetch is aborted too. */
function abortable<T>(fn: (...args: any[]) => Promise<T>, ...args: any[]): Promise<T> {
  const controller = new AbortController();
  const promise: any = fn(...args, controller.signal);
  promise[CANCEL] = () => controller.abort();
  return promise;
}

function* fetchCustomerStampKpisSaga(action: ReturnType<typeof fetchCustomerStampKpisRequest>) {
  try {
    // QA #10 — the comparison window is FE-owned, exactly as in Snapshot / Sales'
    // `fetchSnapshotComparisonSaga`: it follows the SHAPE of the selection (a
    // Monday vs the previous Monday, 7D vs the preceding 7 days, a month vs the
    // previous month), which only the period resolver knows. Today has no dates,
    // which the resolver reads as the store's current day.
    const { startDate, endDate } = action.payload;
    const response: CustomerStampKpisResponse = yield call(abortable, getCustomerStampKpisApi, {
      ...action.payload,
      ...comparisonQueryParams(startDate, endDate),
    });
    yield put(fetchCustomerStampKpisSuccess(response));
  } catch (error: any) {
    yield put(fetchCustomerStampKpisFailure(error?.message || 'Failed to load customer KPIs.'));
  }
}

function* fetchCustomerSegmentsSaga(action: ReturnType<typeof fetchCustomerSegmentsRequest>) {
  try {
    const response: CustomerSegmentBreakdownResponse = yield call(
      abortable,
      getCustomerSegmentBreakdownApi,
      action.payload,
    );
    yield put(fetchCustomerSegmentsSuccess(response));
  } catch (error: any) {
    yield put(fetchCustomerSegmentsFailure(error?.message || 'Failed to load customer segments.'));
  }
}

/* `takeLatest` matters more here than for the KPI slices: sort clicks, page
   jumps and debounced typing can each land while the previous page is still in
   flight, and only the newest query's rows may reach the table. */
function* fetchCustomerDirectorySaga(action: ReturnType<typeof fetchCustomerDirectoryRequest>) {
  try {
    const response: CustomerDirectoryResponse = yield call(
      abortable,
      getCustomerDirectoryApi,
      action.payload,
    );
    yield put(fetchCustomerDirectorySuccess(response));
  } catch (error: any) {
    yield put(fetchCustomerDirectoryFailure(error?.message || 'Failed to load the customer directory.'));
  }
}

function* fetchChannelPerformanceSaga(action: ReturnType<typeof fetchChannelPerformanceRequest>) {
  try {
    const response: ChannelPerformanceResponse = yield call(
      abortable,
      getChannelPerformanceApi,
      action.payload,
    );
    yield put(fetchChannelPerformanceSuccess(response));
  } catch (error: any) {
    yield put(fetchChannelPerformanceFailure(error?.message || 'Failed to load channel performance.'));
  }
}

function* fetchCohortRetentionSaga(action: ReturnType<typeof fetchCohortRetentionRequest>) {
  try {
    const response: CohortRetentionResponse = yield call(
      abortable,
      getCohortRetentionApi,
      action.payload,
    );
    yield put(fetchCohortRetentionSuccess(response));
  } catch (error: any) {
    yield put(fetchCohortRetentionFailure(error?.message || 'Failed to load cohort retention.'));
  }
}

function* fetchSegmentSpendSaga(action: ReturnType<typeof fetchSegmentSpendRequest>) {
  try {
    // QA #27 — the same comparison window as the stamp KPIs (QA #10), so this
    // card's "+$x" compares against the days the date control's "vs …" names
    // (Mon vs the previous Mon), not the day before.
    const { startDate, endDate } = action.payload;
    const response: AvgSpendBySegmentResponse = yield call(
      abortable,
      getAvgSpendBySegmentApi,
      { ...action.payload, ...comparisonQueryParams(startDate, endDate) },
    );
    yield put(fetchSegmentSpendSuccess(response));
  } catch (error: any) {
    yield put(fetchSegmentSpendFailure(error?.message || 'Failed to load segment spend.'));
  }
}

function* fetchDrilldownStatsSaga(action: ReturnType<typeof fetchDrilldownStatsRequest>) {
  try {
    const response: DrilldownStatsResponse = yield call(abortable, getDrilldownStatsApi, action.payload);
    yield put(fetchDrilldownStatsSuccess(response));
  } catch (error: any) {
    yield put(fetchDrilldownStatsFailure(error?.message || 'Failed to load the drill-down.'));
  }
}

function* fetchDrilldownCustomersSaga(action: ReturnType<typeof fetchDrilldownCustomersRequest>) {
  try {
    const response: DrilldownCustomersResponse = yield call(abortable, getDrilldownCustomersApi, action.payload);
    yield put(fetchDrilldownCustomersSuccess(response));
  } catch (error: any) {
    yield put(fetchDrilldownCustomersFailure(error?.message || 'Failed to load the customer list.'));
  }
}

/**
 * CUST-7 / 16 / 17. `takeLatest` matters more here than elsewhere: clicking
 * quickly down a list would otherwise let an earlier customer's response land
 * after a later one and show the wrong profile.
 */
function* fetchCustomerDetailsSaga(
  action: ReturnType<typeof fetchCustomerDetailsRequest>,
) {
  try {
    const response: CustomerDetailsResponse = yield call(
      abortable,
      getCustomerDetailsApi,
      action.payload,
    );
    yield put(fetchCustomerDetailsSuccess(response));
  } catch (error: any) {
    yield put(
      fetchCustomerDetailsFailure(
        error?.message || 'Failed to load this customer.',
      ),
    );
  }
}

function* fetchAcquisitionSourceSaga(
  action: ReturnType<typeof fetchAcquisitionSourceRequest>,
) {
  try {
    const response: AcquisitionSourceResponse = yield call(
      abortable,
      getAcquisitionSourceApi,
      action.payload,
    );
    yield put(fetchAcquisitionSourceSuccess(response));
  } catch (error: any) {
    yield put(
      fetchAcquisitionSourceFailure(
        error?.message || 'Failed to load acquisition sources.',
      ),
    );
  }
}

export default function* customersReportSaga() {
  yield all([
    takeLatest(FETCH_CUSTOMER_STAMP_KPIS_REQUEST, fetchCustomerStampKpisSaga),
    takeLatest(FETCH_CUSTOMER_SEGMENTS_REQUEST, fetchCustomerSegmentsSaga),
    takeLatest(FETCH_CUSTOMER_DIRECTORY_REQUEST, fetchCustomerDirectorySaga),
    takeLatest(FETCH_CHANNEL_PERFORMANCE_REQUEST, fetchChannelPerformanceSaga),
    takeLatest(FETCH_COHORT_RETENTION_REQUEST, fetchCohortRetentionSaga),
    takeLatest(FETCH_SEGMENT_SPEND_REQUEST, fetchSegmentSpendSaga),
    takeLatest(FETCH_DRILLDOWN_STATS_REQUEST, fetchDrilldownStatsSaga),
    takeLatest(FETCH_DRILLDOWN_CUSTOMERS_REQUEST, fetchDrilldownCustomersSaga),
    takeLatest(FETCH_CUSTOMER_DETAILS_REQUEST, fetchCustomerDetailsSaga),
    takeLatest(FETCH_ACQUISITION_SOURCE_REQUEST, fetchAcquisitionSourceSaga),
  ]);
}
